<template>
  <div class="video-lib">
    <header class="toolbar">
      <button class="icon-btn back-btn" @click="$router.back()" aria-label="返回上一頁">
        <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M15 6l-6 6 6 6" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>

      <div class="title-center">
        <h2 class="toolbar-title">{{ subtopicName || '小主題影片' }}</h2>
        <p class="sub">{{ subDesc || '點任一影片前往播放頁' }}</p>
      </div>
    </header>


    <section class="list">
      <div v-if="loading" class="hint">載入中…</div>
      <div v-else-if="videos.length === 0" class="hint">這個主題目前沒有影片</div>

      <div class="grid">
        <article v-for="v in videos" :key="v.id" class="card" @click="gotoPlay(v.id)">
          <img :src="v.thumb_url || placeholder16x9" alt="" class="thumb16x9" loading="lazy" />
          <div class="meta">
            <div class="vtitle">{{ v.title }}</div>
            <!-- <div class="tags">
              <span v-for="k in (v.keywords || [])" :key="k" class="tag">#{{ k }}</span>
            </div> -->
          </div>
        </article>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { supabase } from '@/lib/supabase'
import i18n from '@/i18n'   // ← 路線A，直接用全域 i18n

const route = useRoute()
const router = useRouter()
const subId = route.params.subId

// 目前語言（兼容 string/ref）
const currentLocale = computed(() => {
  const l = i18n.global.locale
  return typeof l === 'string' ? l : (l?.value || 'zh-TW')
})

const subtopicName = ref('')
const subDesc = ref('')
const videos = ref([])
const loading = ref(false)
const placeholder16x9 = 'https://placehold.co/480x270?text=Video'

/* -----------------------------
 * 小主題名稱：video_subtopic_i18n
 * 先 zh-TW 入庫，再用當前語言覆蓋（fallback）
 * ----------------------------*/
async function loadSubtopicName() {
  const L = currentLocale.value

  const { data, error } = await supabase
    .from('video_subtopic_i18n')
    .select('subtopic_id, locale, name, sub_name')
    .eq('subtopic_id', subId)
    .in('locale', [L, 'zh-TW'])

  if (error) {
    console.error('[loadSubtopicName]', error)
    subtopicName.value = ''
    subDesc.value = ''
    return
  }

  const zh = (data || []).find(r => r.locale === 'zh-TW')
  const cur = (data || []).find(r => r.locale === L)

  subtopicName.value = cur?.name || zh?.name || ''
  subDesc.value = cur?.sub_name || zh?.sub_name || ''
}

/* --------------------------------------
 * 影片清單：videos(base) + video_i18n(翻譯)
 * 先抓 base（排序/created_at），再用 i18n 覆蓋 title/thumb
 * -------------------------------------*/
async function loadVideos() {
  const L = currentLocale.value

  // A) base videos（排序來源）
  const { data: base, error: e1 } = await supabase
    .from('videos')
    .select('id, thumb_url, storage_path, created_at, sort')
    .eq('subtopic_id', subId)
    .order('sort', { ascending: true, nullsFirst: false })
    .order('created_at', { ascending: false })
    .range(0, 3) // 0~3 (含 3)；想拿 4 部就保留這行

  if (e1) {
    console.error('[loadVideos base]', e1)
    videos.value = []
    return
  }
  if (!base || base.length === 0) { videos.value = []; return }

  const ids = base.map(v => v.id)

  // B) i18n：當前語言 + zh-TW
  const { data: i18nRows, error: e2 } = await supabase
    .from('video_i18n')
    .select('video_id, locale, title, thumb_url')
    .in('video_id', ids)
    .in('locale', [L, 'zh-TW'])

  if (e2) console.error('[loadVideos i18n]', e2)

  // 合併（zh-TW 先入、L 覆蓋）
  const titleMap = new Map()
  const thumbMap = new Map()

  ;(i18nRows || []).filter(r => r.locale === 'zh-TW').forEach(r => {
    titleMap.set(r.video_id, r.title || '')
    thumbMap.set(r.video_id, r.thumb_url || '')
  })
  ;(i18nRows || []).filter(r => r.locale === L).forEach(r => {
    if (r.title) titleMap.set(r.video_id, r.title)
    if (r.thumb_url) thumbMap.set(r.video_id, r.thumb_url)
  })

  videos.value = base.map(v => ({
    id: v.id,
    title: titleMap.get(v.id) || '(未翻譯)',
    thumb_url: thumbMap.get(v.id) || v.thumb_url || '',
  }))
}

function gotoPlay(id) { router.push({ name: 'VideoPlay', params: { id } }) }

async function boot() {
  loading.value = true
  await loadSubtopicName()
  await loadVideos()
  loading.value = false
}

onMounted(boot)

// 子路由或語言切換都重載
watch(() => route.params.subId, boot)
watch(currentLocale, async (n, o) => { if (n !== o) await boot() })
</script>


<style scoped>
.toolbar {
  position: relative;
  background:#fff;
  border:1px solid #eee;
  border-radius:14px;
  padding:14px 48px;
  min-height:68px;
}

.back-btn {
  position:absolute;
  left:12px;
  top:50%;
  transform:translateY(-50%);
  display:inline-flex;
  align-items:center;
  justify-content:center;
  width:36px; height:36px;
  border-radius:10px;
  border:1px solid #e5e7eb;
  background:#fff;
  color:#111;
  cursor:pointer;
}

.title-center {
  position:absolute;
  left:50%; top:50%;
  transform:translate(-50%, -50%);
  text-align:center;
  width:calc(100% - 120px);
  pointer-events:none;
}
.title-center > * { pointer-events:auto; }
.toolbar-title { margin:0; font-size:20px; font-weight:700; line-height:1.2; }
.sub { color:#666; margin-top:4px; font-size:14px; }

.grid { display:grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap:12px; }
@media (max-width:1200px){ .grid{ grid-template-columns: repeat(2, minmax(0,1fr)); } }
.card { background:#fafafa; border:1px solid #eee; border-radius:12px; overflow:hidden; cursor:pointer; transition: transform .12s ease, box-shadow .12s ease; }
.card:hover { transform: translateY(-1px); box-shadow: 0 10px 24px rgba(0,0,0,.06); }
.thumb16x9 { width:100%; aspect-ratio:16/9; object-fit:cover; display:block; background:#ddd; }
.meta { padding:10px 12px; }
.vtitle { font-weight:600; margin-bottom:6px; }
.tag { font-size:12px; color:#666; margin-right:6px; }
</style>
